import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import TrustBar from "../components/TrustBar";
import CredibilityStrip from "../components/CredibilityStrip";
import RecentlyViewed from "../components/RecentlyViewed";
import Testimonials from "../components/Testimonials";
import Seo, { organizationJsonLd } from "../components/Seo";

const HERO_IMG = "https://images.pexels.com/photos/19186713/pexels-photo-19186713.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=940";
const EDITORIAL_IMG = "https://images.unsplash.com/photo-1713425884527-66c2e63dfb39?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const TEXTURE_IMG = "https://images.pexels.com/photos/8502484/pexels-photo-8502484.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const MARQUEE_TAGS = [
  "100% Authentic", "Money-back guarantee", "Free shipping over $200",
  "14-day returns", "Worldwide delivery",
  "100% Authentic", "Money-back guarantee", "Free shipping over $200",
  "14-day returns", "Worldwide delivery",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [discover, setDiscover] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, b, c] = await Promise.all([
          api.get("/products", { params: { featured: true, limit: 4 } }),
          api.get("/products", { params: { sort: "newest", limit: 8 } }),
          api.get("/products", { params: { max_price: 1000, sort: "price_asc", limit: 4 } }),
        ]);
        setFeatured(a.data || []);
        setLatest(b.data || []);
        setDiscover(c.data || []);
      } catch (_) {}
    })();
  }, []);

  return (
    <div data-testid="home-page">
      <Seo jsonLd={organizationJsonLd()} />
      {/* HERO */}
      <section className="pt-20 relative" data-testid="hero-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-16 md:pb-24 grid grid-cols-12 gap-6 md:gap-12 items-end">
          <div className="col-span-12 md:col-span-5 fade-up">
            <p className="eyebrow">The Epitome of Luxury — MMXXVI</p>
            <h1 className="font-display mt-6 text-[12vw] md:text-[7.5vw] leading-[0.92] tracking-tighter font-black">
              Designer bags. <em className="font-medium">Direct</em> to your door.
            </h1>
            <p className="mt-8 max-w-md text-[var(--mn-ink-2)] leading-relaxed">
              Elegant Affairs is a curated edit of designer luxury handbags —
              authenticated, beautifully shot, shipped worldwide. The bag you want,
              without the boutique wait.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" className="btn-primary" data-testid="hero-shop-cta">
                Shop the Collection <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link to="/about#concierge" className="btn-ghost" data-testid="hero-about-cta">
                Style Advice
              </Link>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7 fade-up-2">
            <div className="relative overflow-hidden aspect-[4/5] md:aspect-[5/6]">
              <img src={HERO_IMG} alt="Editorial" className="w-full h-full object-cover" />
              <div className="absolute bottom-8 left-8 right-8 flex justify-between items-end text-white">
                <div className="font-mono-luxe text-xs">N° 014 / 2026</div>
                <div className="text-right">
                  <p className="eyebrow text-white/80">Cover</p>
                  <p className="font-display italic text-2xl mt-1">La Silhouette</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-[var(--mn-border)] py-5 overflow-hidden">
          <div className="marquee">
            {MARQUEE_TAGS.map((t, i) => (
              <span key={i} className="font-display italic text-2xl md:text-3xl text-[var(--mn-ink)]/80 whitespace-nowrap">
                {t} <span className="text-[var(--mn-accent)]">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CREDIBILITY STRIP — thin, above-the-fold positioning facts */}
      <CredibilityStrip />

      {/* TRUST BAR — detailed guarantees */}
      <TrustBar />

      {/* FEATURED */}
      <section className="py-24 md:py-32" data-testid="featured-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <p className="eyebrow">The Edit</p>
              <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">Featured pieces.</h2>
            </div>
            <Link to="/shop" className="text-sm uppercase tracking-[0.22em] flex items-center gap-2 hover:text-[var(--mn-accent)]" data-testid="featured-view-all">
              View all <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>

          {featured.length === 0 ? (
            <EmptyState message="No featured bags yet. Add some via Manage." />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-12 gap-6 md:gap-10">
              {featured.slice(0, 4).map((p, i) => (
                <div
                  key={p.id}
                  className={`col-span-2 md:col-span-${i === 0 ? 6 : i === 1 ? 6 : i === 2 ? 4 : 4}`}
                >
                  <ProductCard product={p} index={i} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* EDITORIAL SPLIT */}
      <section className="bg-[var(--mn-ink)] text-[var(--mn-bg)] relative overflow-hidden" data-testid="editorial-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-40 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow text-white/60">Editorial</p>
            <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight">
              Carried by women <em className="italic font-light">who decide.</em>
            </h2>
            <p className="mt-8 text-white/70 max-w-md leading-relaxed">
              We refresh the edit each month with the bags we'd actually carry —
              designer staples, statement pieces, and the occasional rarity. All
              authenticated, all shipped within days.
            </p>
            <Link to="/shop" className="inline-flex items-center gap-2 mt-10 text-sm uppercase tracking-[0.22em] border-b border-white/40 pb-1 hover:border-white" data-testid="editorial-cta">
              Browse the Edit <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="col-span-12 md:col-span-7">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={EDITORIAL_IMG} alt="Editorial" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* LATEST */}
      <section className="py-24 md:py-32" data-testid="latest-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow">Just Arrived</p>
              <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">New arrivals.</h2>
            </div>
          </div>

          {latest.length === 0 ? (
            <EmptyState message="The catalogue awaits its first piece." />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {latest.slice(0, 8).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}

          <RecentlyViewed />

          <Testimonials />
        </div>
      </section>

      {/* DISCOVER · UNDER $1,000 */}
      {discover.length > 0 && (
        <section className="bg-[var(--mn-bg-alt,#f5f0e8)] py-24 md:py-32" data-testid="discover-section">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <p className="eyebrow">Discover</p>
                <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">
                  Beautifully <em className="font-light">attainable.</em>
                </h2>
                <p className="mt-5 max-w-xl text-[var(--mn-ink-soft,#5c5c59)] text-sm md:text-base leading-relaxed">
                  An invitation to the house. Authenticated designer bags, all under <span className="font-medium">$1,000</span>.
                </p>
              </div>
              <Link
                to="/shop?max_price=1000&sort=price_asc"
                className="text-sm uppercase tracking-[0.22em] flex items-center gap-2 hover:text-[var(--mn-accent)] self-start md:self-auto"
                data-testid="discover-view-all"
              >
                Shop the Edit
                <span aria-hidden>→</span>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {discover.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CRAFT BANNER */}
      <section className="relative h-[60vh] md:h-[70vh] overflow-hidden" data-testid="craft-banner">
        <img src={TEXTURE_IMG} alt="Designer leather" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 pb-16 md:pb-24 w-full">
            <p className="eyebrow text-white/70">The Promise</p>
            <p className="font-display text-white text-4xl md:text-7xl mt-4 max-w-3xl leading-[1.05] tracking-tight">
              Authenticated. Beautifully packaged. <em className="font-light">At your door.</em>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="border border-dashed border-[var(--mn-border-strong)] py-20 text-center" data-testid="empty-state">
      <p className="font-display text-2xl">{message}</p>
      <Link to="/shop" className="btn-ghost mt-6" data-testid="empty-shop-cta">Browse the Edit</Link>
    </div>
  );
}
